//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  ChangeEvent,
  useState,
  useCallback,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import info from '../../../icons/infoIcon.svg';

import LoopDataService from '../../../utils/LoopDataService';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import TenantDataService from '../../../utils/TenantDataService';
import { useUserAuth } from '../../../UserAuth';

export const COLOR_OPTIONS = [
  { id: 1, color: '#E91E63', name: 'Pink' },
  { id: 2, color: '#9C27B0', name: 'Purple' },
  { id: 3, color: '#3F51B5', name: 'Indigo' },
  { id: 4, color: '#2196F3', name: 'Blue' },
  { id: 5, color: '#00BCD4', name: 'Cyan' },
  { id: 6, color: '#4CAF50', name: 'Green' },
  { id: 7, color: '#FF9800', name: 'Orange' },
  { id: 8, color: '#FFD54F', name: 'Yellow' },
];

function DefineLoop({
  mode,
  loopName,
  setLoopName,
  description,
  setDescription,
  isSubmitted,
  defineError,
  setIsSubmitted,
  onNextStep,
  warning,
  setWarning,
  demoDataType,
  setdemoDataType,
  importDemoData,
  setImportDemoData,
  selectedColor,
  setSelectedColor,
}: {
  mode: string;
  loopName: string;
  setLoopName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  isSubmitted: boolean;
  defineError: string;
  setIsSubmitted: (submitted: boolean) => void;
  onNextStep: () => void;
  warning: string;
  setWarning: (warning: string) => void;
  demoDataType: string;
  setdemoDataType: (type: string) => void;
  importDemoData: boolean;
  setImportDemoData: (importDemoData: boolean) => void;
  selectedColor: string;
  setSelectedColor: (color: string) => void;
}) {
  const { t } = useTranslation();
  const [nameLimitWarning, setNameLimitWarning] = useState('');
  const [descriptionLimitWarning, setDescriptionLimitWarning] = useState('');
  const [prefixWarning, setPrefixWarning] = useState('');
  const [minLengthWarning, setMinLengthWarning] = useState('');
  const [specialCharWarning, setSpecialCharWarning] = useState('');
  const [learningLoopLimitWarning, setLearningLoopLimitWarning] = useState('');
  const specialCharTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [colorOptions, setColorOptions] = useState<string[]>(
    COLOR_OPTIONS.map((o) => o.color),
  );
  const [hover, setHover] = useState(false);

  const getInitials = (name: string) =>
    name
      .trim()
      .split(/\s+/)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .slice(0, 2)
      .join('') || 'TS';

  const MAX_LOOP_NAME_LENGTH = 20;
  const MAX_DESCRIPTION_LENGTH = 120;
  const MIN_NAME_LENGTH = 3;

  const { teamspaceId, tenantId } = useTeamspaceContext();
  const { user, isAuthenticated } = useUserAuth();
  const loopService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const limitCheckCounterRef = useRef(0);

  const checkAndSetLearningLoopLimit = useCallback(
    (enabled: boolean) => {
      if (
        enabled &&
        teamspaceId &&
        typeof loopService.checkLearningLoopLimits === 'function'
      ) {
        setWarning('');
        const requestId = ++limitCheckCounterRef.current;
        loopService
          .checkLearningLoopLimits(teamspaceId)
          .then((status) => {
            if (requestId !== limitCheckCounterRef.current) return;
            if (status?.teamSpaceLimitReached) {
              const msg = t('learning-loop-teamspace-limit-reached', {
                limit: status.teamSpaceLimit,
              });
              setLearningLoopLimitWarning(msg);
              setWarning(msg);
            } else if (status?.globalLimitReached) {
              const msg = t('learning-loop-global-limit-reached', {
                limit: status.globalLimit,
              });
              setLearningLoopLimitWarning(msg);
              setWarning(msg);
            } else {
              setLearningLoopLimitWarning('');
            }
          })
          .catch(() => {
            if (requestId !== limitCheckCounterRef.current) return;
            setLearningLoopLimitWarning('');
          });
      } else {
        setLearningLoopLimitWarning('');
      }
    },
    [teamspaceId, loopService, t, setWarning],
  );

  useEffect(() => {
    if (mode !== 'teamspace') return;
    tenantDataService
      .getTeamspaceIconColorOptions()
      .then((colors) => {
        if (Array.isArray(colors) && colors.length > 0) {
          const colorStrings = colors.map((c: { color: string }) => c.color);
          setColorOptions(colorStrings);
          if (selectedColor === COLOR_OPTIONS[0].color) {
            setSelectedColor(colorStrings[0]);
          }
        }
      })
      .catch(() => {
        // fallback to hardcoded COLOR_OPTIONS
      });
  }, [mode, selectedColor, setSelectedColor, tenantDataService]);

  // Check for uniqueness of first 5 chars for Loops or teamspace name instantly
  useEffect(() => {
    if (specialCharWarning) {
      return;
    }

    const checkUniqueness = async () => {
      setPrefixWarning('');
      setMinLengthWarning('');

      if (loopName.length > 0 && loopName.length < MIN_NAME_LENGTH) {
        setWarning(
          mode === 'loop'
            ? t('loop-name-min-length', { min: MIN_NAME_LENGTH })
            : t('teamspace-name-min-length', { min: MIN_NAME_LENGTH }),
        );
        return;
      }

      if (mode === 'loop' && loopName.length >= MIN_NAME_LENGTH) {
        const sanitizedInput = loopName.replace(/\s/g, '_').substring(0, 5);
        try {
          if (teamspaceId) {
            const response = await loopService.loopUniqueName(
              teamspaceId,
              sanitizedInput,
            );
            const loops = response?._embedded?.loops || [];
            const isDuplicate = loops.some(
              (loop: any) =>
                loop.displayName?.toLowerCase().substring(0, 5) ===
                sanitizedInput.toLowerCase(),
            );
            if (isDuplicate) {
              setWarning(t('loop-name-already'));
            } else {
              setWarning('');
            }
          }
        } catch (e) {
          setPrefixWarning('');
          setWarning('');
        }
      } else if (mode === 'teamspace' && loopName.length >= MIN_NAME_LENGTH) {
        const sanitizedInput = loopName.replace(/\s/g, '_');
        if (tenantId) {
          try {
            const response =
              (await tenantDataService.checkIfTeamSpaceNameIsUnique(
                tenantId,
                sanitizedInput,
              )) as any;
            const isTaken =
              typeof response === 'object' &&
              response !== null &&
              'data' in response
                ? response.data
                : response;
            if (isTaken === false) {
              setWarning(t('teamspace-name-already-exists'));
            } else {
              setWarning('');
            }
          } catch (e) {
            setWarning('');
          }
        }
      } else {
        setWarning('');
      }
    };

    checkUniqueness();
  }, [
    loopName,
    mode,
    tenantId,
    t,
    teamspaceId,
    setWarning,
    loopService,
    tenantDataService,
    specialCharWarning,
  ]);

  useEffect(() => {
    return () => {
      if (specialCharTimerRef.current)
        clearTimeout(specialCharTimerRef.current);
    };
  }, []);

  const handleLoopNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubmitted(false);
    setPrefixWarning('');
    setMinLengthWarning('');

    const input = event.target.value;

    // Allow only letters, numbers, and underscores (no special characters)
    if (!/^[a-zA-Z0-9_]*$/.test(input)) {
      // Show transient warning but don't block submission since char is rejected
      setSpecialCharWarning(t('specialCharactersNotAllowed'));
      // Clear any previous timer to avoid race conditions on rapid input
      if (specialCharTimerRef.current)
        clearTimeout(specialCharTimerRef.current);
      // Auto-clear the warning after 2 seconds
      specialCharTimerRef.current = setTimeout(() => {
        setSpecialCharWarning('');
        specialCharTimerRef.current = null;
      }, 2000);
      return; // 🚨 Do not update loopName, keep last valid one
    }

    // Replace spaces with underscores
    const sanitizedInput = input.replace(/\s/g, '_');

    let validInput = sanitizedInput;
    if (validInput.length > MAX_LOOP_NAME_LENGTH) {
      validInput = validInput.substring(0, MAX_LOOP_NAME_LENGTH);
      setNameLimitWarning(
        t('maxCharacterLimitExceeded', { limit: MAX_LOOP_NAME_LENGTH }),
      );
    } else {
      setNameLimitWarning('');
    }

    setLoopName(validInput);
    setWarning(''); // ✅ clear warnings only if input is valid

    if (validInput.length > 0 && validInput.length < MIN_NAME_LENGTH) {
      setMinLengthWarning(
        mode === 'loop'
          ? t('loop-name-min-length', { min: MIN_NAME_LENGTH })
          : t('teamspace-name-min-length', { min: MIN_NAME_LENGTH }),
      );
    } else {
      setMinLengthWarning('');
    }
  };

  const handleDescriptionChange = (
    e: ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    let input = e.target.value;
    if (input.length > MAX_DESCRIPTION_LENGTH) {
      input = input.substring(0, MAX_DESCRIPTION_LENGTH);
      setDescriptionLimitWarning(
        t('max-description-character-limit-exceeded', {
          limit: MAX_DESCRIPTION_LENGTH,
        }),
      );
    } else {
      setDescriptionLimitWarning('');
    }
    setDescription(input);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (
        loopName &&
        !warning &&
        !nameLimitWarning &&
        !prefixWarning &&
        !minLengthWarning &&
        !loopNameLimitExceeded &&
        !descriptionLimitWarning &&
        !descriptionLimitExceeded &&
        !learningLoopLimitWarning
      ) {
        onNextStep();
      } else {
        setIsSubmitted(true);
      }
    }
  };

  const loopNameLimitExceeded = loopName.length > MAX_LOOP_NAME_LENGTH;
  const descriptionLimitExceeded = description.length > MAX_DESCRIPTION_LENGTH;
  const isLoopNameEmpty = loopName.length === 0;

  return (
    <div
      id="defineLoop"
      data-testid="define-loop-container"
      className="m-4 flex flex-col gap-1.5"
    >
      {mode === 'teamspace' && (
        <div
          id="id-define-loop-icon-section"
          data-testid="define-loop-icon-section"
          className="mb-4"
        >
          <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-white">
            {t('teamspace-icon-label')}
          </label>
          <div
            id="id-define-loop-icon-picker"
            data-testid="define-loop-icon-picker"
            className="flex items-center gap-4"
          >
            <div
              id="id-define-loop-icon-preview"
              data-testid="define-loop-icon-preview"
              className="flex size-12 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white"
              style={{ backgroundColor: selectedColor }}
            >
              {getInitials(loopName)}
            </div>
            <div
              id="id-define-loop-color-options"
              data-testid="define-loop-color-options"
              className="flex flex-wrap gap-2"
            >
              {colorOptions.map((color, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`size-8 rounded-full transition-transform hover:scale-110 ${
                    selectedColor === color
                      ? 'scale-110 ring-2 ring-gray-400 ring-offset-2'
                      : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        id="id-define-loop-name-section"
        data-testid="define-loop-name-section"
        className="mb-4"
      >
        <label
          htmlFor="loopinput"
          aria-label={t('loop-name-label')}
          className="mb-2 flex items-center gap-1 text-sm text-carbon-textSecondary dark:text-brand-disabled"
        >
          {mode === 'loop' ? t('enterLoopName') : t('enter-teamspace-name')}
          <img src={info} alt={t('Info')} className="inline-block size-4" />
          <span
            id="id-define-loop-required-indicator"
            data-testid="define-loop-required-indicator"
            className="ml-0.5 text-red-500"
          >
            *
          </span>
        </label>
        <input
          id="loopinput"
          aria-label={t('loop-name-input')}
          type="text"
          aria-required="true"
          value={loopName}
          onChange={handleLoopNameChange}
          onKeyDown={handleKeyDown}
          name="loopname"
          placeholder=""
          className={`h-[30px] w-full rounded border bg-white px-3 py-2 text-sm text-carbon-dark focus:border-brand-interactive focus:outline-none dark:bg-carbon-darkPanel dark:text-white ${
            loopNameLimitExceeded || prefixWarning || minLengthWarning
              ? 'border-red-500 dark:border-red-400'
              : 'border-carbon-textDisabled dark:border-carbon-borderDark'
          }`}
        />
        <div
          id="id-define-loop-name-char-count"
          data-testid="define-loop-name-char-count"
          className="mt-1 text-right text-xs text-brand-interactive"
        >
          {loopName.length}/{MAX_LOOP_NAME_LENGTH} {t('characters')}
        </div>
      </div>

      {specialCharWarning && (
        <div
          id="id-define-loop-special-char-warning"
          data-testid="define-loop-special-char-warning"
          aria-live="assertive"
          className="-mt-4 flex w-4/5 flex-col pb-5 text-left text-xs font-normal italic text-carbon-errorText"
        >
          {specialCharWarning}
        </div>
      )}

      {(nameLimitWarning ||
        minLengthWarning ||
        (defineError && isSubmitted) ||
        (warning && warning !== learningLoopLimitWarning) ||
        prefixWarning ||
        loopNameLimitExceeded ||
        (isSubmitted && isLoopNameEmpty)) && (
        <div
          id="allNameWarnings"
          data-testid="define-loop-name-warnings"
          aria-label={t('loop-name-all-warnings')}
          className="-mt-4 flex w-4/5 flex-col pb-5 text-left text-xs font-normal italic text-carbon-errorText"
        >
          {nameLimitWarning && (
            <div
              id="id-define-loop-name-limit-warning"
              data-testid="define-loop-name-limit-warning"
              aria-live="assertive"
            >
              {nameLimitWarning}
            </div>
          )}
          {minLengthWarning && (
            <div
              id="id-define-loop-min-length-warning"
              data-testid="define-loop-min-length-warning"
              aria-live="assertive"
            >
              {minLengthWarning}
            </div>
          )}
          {defineError && isSubmitted && (
            <div
              id="id-define-loop-define-error"
              data-testid="define-loop-define-error"
              aria-live="assertive"
            >
              {defineError}
            </div>
          )}
          {warning && warning !== learningLoopLimitWarning && (
            <div
              id="id-define-loop-warning"
              data-testid="define-loop-warning"
              aria-live="assertive"
            >
              {warning}
            </div>
          )}
          {prefixWarning && (
            <div
              id="id-define-loop-prefix-warning"
              data-testid="define-loop-prefix-warning"
              aria-live="assertive"
            >
              {prefixWarning}
            </div>
          )}
          {loopNameLimitExceeded && (
            <div
              id="id-loopNameError"
              data-testid="loopNameError"
              aria-live="assertive"
            >
              {t('maxCharacterLimitExceeded', { limit: MAX_LOOP_NAME_LENGTH })}
            </div>
          )}
          {isSubmitted && isLoopNameEmpty && (
            <div
              id="id-define-loop-empty-name-error"
              data-testid="define-loop-empty-name-error"
              aria-live="assertive"
            >
              {mode === 'loop'
                ? t('loopNameCannotBeEmpty')
                : t('teamspace-name-cannot-beempty')}
            </div>
          )}
        </div>
      )}

      <div
        id="loopDescription"
        data-testid="define-loop-description-section"
        aria-label={t('loop-description-label')}
        className="mb-4"
      >
        <label
          htmlFor="descInput"
          aria-label={t('loop-description-optional')}
          className="mb-2 block text-sm text-carbon-textSecondary dark:text-brand-disabled"
        >
          {t('description-label')}
        </label>
        <textarea
          id="descInput"
          aria-label={t('loop-description-input')}
          value={description}
          onChange={handleDescriptionChange}
          name="description"
          rows={6}
          placeholder=""
          className={`w-full resize-none rounded border bg-white px-3 py-2 text-sm text-carbon-dark focus:border-brand-interactive focus:outline-none dark:bg-carbon-darkPanel dark:text-white ${
            descriptionLimitExceeded
              ? 'border-red-500 dark:border-red-400'
              : 'border-carbon-textDisabled dark:border-carbon-borderDark'
          }`}
        />
        <div
          id="id-define-loop-desc-char-count"
          data-testid="define-loop-desc-char-count"
          className="mt-1 text-right text-xs text-brand-interactive"
        >
          {description.length}/{MAX_DESCRIPTION_LENGTH} {t('characters')}
        </div>
      </div>
      {descriptionLimitWarning && (
        <div
          id="descriptionLimitWarning"
          data-testid="define-loop-desc-limit-warning"
          aria-label={t('loop-description-limit-warning')}
          aria-live="assertive"
          className="flex flex-col text-left text-xs font-normal italic text-carbon-errorText"
        >
          {descriptionLimitWarning}
        </div>
      )}
      {descriptionLimitExceeded && (
        <div
          id="limitExceeded"
          data-testid="descriptionError"
          aria-label={t('loop-description-error')}
          aria-live="assertive"
          className="flex flex-col text-left text-xs font-normal italic text-carbon-errorText"
        >
          {t('maxCharacterLimitExceeded', { limit: MAX_DESCRIPTION_LENGTH })}
        </div>
      )}

      {mode === 'loop' && (
        <div
          id="id-define-loop-demo-data-section"
          data-testid="define-loop-demo-data-section"
        >
          <label className="mb-1 block text-base font-normal text-carbon-demoLabel">
            {t('load-demo-data')}
          </label>
          <div
            id="id-define-loop-demo-toggle-row"
            data-testid="define-loop-demo-toggle-row"
            className="mb-2 flex items-center"
          >
            <div
              id="id-define-loop-demo-toggle"
              data-testid="define-loop-demo-toggle"
              className="inline-flex cursor-pointer items-center"
              onClick={() => {
                const newValue = !importDemoData;
                setImportDemoData(newValue);
                setdemoDataType(newValue ? 'application' : '');
                checkAndSetLearningLoopLimit(newValue);
              }}
              role="checkbox"
              aria-checked={importDemoData}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const newValue = !importDemoData;
                  setImportDemoData(newValue);
                  setdemoDataType(newValue ? 'application' : '');
                  checkAndSetLearningLoopLimit(newValue);
                }
              }}
            >
              <div
                id="id-define-loop-toggle-track"
                data-testid="define-loop-toggle-track"
                className={`relative h-4 w-8 rounded-full transition-colors ${
                  importDemoData ? 'bg-brand-link' : 'bg-brand-toggleOff'
                }`}
              >
                <div
                  id="id-define-loop-toggle-thumb"
                  data-testid="define-loop-toggle-thumb"
                  className={`absolute top-0.5 size-3 rounded-full bg-white transition-all ${
                    importDemoData ? 'left-[18px]' : 'left-0.5'
                  }`}
                />
              </div>
              <label className="text-black/87 ml-2 cursor-pointer p-[2px_2px_4px_2px] text-[13px] font-normal leading-4">
                {t('import-demo-data-to-loop')}
              </label>
            </div>

            <div
              id="tooltip-container"
              data-testid="define-loop-tooltip-container"
              className="relative inline-block pt-0.5"
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
            >
              <img
                id="tooltip-icon"
                src={info}
                alt={t('Info')}
                className="inline-block size-4 cursor-pointer"
              />
              <div
                id="tooltip-text"
                data-testid="define-loop-tooltip-text"
                className={`absolute left-full top-1/2 z-10 ml-2 w-[25ch] -translate-y-1/2 rounded bg-white p-[6px_8px] text-xs font-normal leading-[18px] tracking-[0.16px] text-carbon-tooltipText shadow-[rgba(0,0,0,0.2)_2px_4px_8px_0px] transition-opacity duration-300 ${
                  hover ? 'visible opacity-100' : 'invisible opacity-0'
                }`}
              >
                {t('import-demo-data-tooltip')}
              </div>
            </div>
          </div>
          {importDemoData && (
            <div
              id="id-define-loop-demo-data-message"
              data-testid="define-loop-demo-data-message"
              className="ml-9 mt-1"
            >
              {learningLoopLimitWarning ? (
                <span
                  id="id-define-loop-limit-warning"
                  data-testid="define-loop-limit-warning"
                  className="text-[13px] font-normal leading-4 text-red-600"
                >
                  {learningLoopLimitWarning}
                </span>
              ) : (
                <span
                  id="id-define-loop-demo-data-type-text"
                  data-testid="define-loop-demo-data-type-text"
                  className="text-[13px] font-normal leading-4 text-brand-link"
                >
                  {t('default-demo-data-type-message')}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {/* ================================================ */}
    </div>
  );
}

export default DefineLoop;
